import { useCallback, useEffect, type ReactElement } from "react";
import type { StampDto } from "@sikilesi/protocol";
import { Link } from "../components/Link";
import { Thread } from "../components/Thread";
import { TopicCard } from "../components/TopicCard";
import { useTopicThread } from "../hooks/useTopicThread";
import { prefersReducedMotion } from "../lib/motion";
import { up } from "../lib/tr";

function focusIdFromHash(hash: string): string | null {
  const m = /^#y-([0-9a-f-]{36})$/.exec(hash);
  return m?.[1] ?? null;
}

export function TopicPage({ id, onStamped }: { id: string; onStamped: (s: StampDto) => void }): ReactElement {
  const { topic, comments, status, setTopic } = useTopicThread(id);
  const focusId = focusIdFromHash(window.location.hash);

  useEffect(() => {
    if (topic) document.title = `${topic.title} · sikile.si`;
  }, [topic]);

  useEffect(() => {
    if (status !== "ready") return;
    const target = focusId ? document.getElementById(`y-${focusId}`) : null;
    if (target) {
      target.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "center" });
      target.focus({ preventScroll: true });
    }
  }, [status, focusId]);

  const stamped = useCallback(
    (s: StampDto) => {
      onStamped(s);
      if (topic) setTopic({ ...topic, stamp_count: s.stamp_count, level: s.level });
    },
    [onStamped, setTopic, topic],
  );

  if (status === "notfound") {
    return (
      <section className="z-feed">
        <h1 className="z-banner">{up("bulgu bulunamadı")}</h1>
        <p className="z-banner">Bu kupür akıştan kaldırılmış, arşive girmiş ya da yalnızca terminalde duruyor.</p>
        <Link href="/" className="z-btn">
          {up("akışa dön")}
        </Link>
      </section>
    );
  }
  if (status === "error") return <p className="z-banner" role="alert">Bulgu yüklenemedi.</p>;
  if (!topic) return <p className="z-banner">{up("dosya açılıyor")}…</p>;

  return (
    <section className="z-feed z-topic">
      <Link href="/" className="z-back">
        ← {up("akış")}
      </Link>
      <TopicCard topic={topic} standalone onStamped={stamped} />
      <h2 className="z-thread__title">{up("tartışma")}</h2>
      <Thread comments={comments} focusId={focusId} />
    </section>
  );
}
